import {
  Anchor,
  AppShell,
  Breadcrumbs,
  Burger,
  Button,
  Group,
  NavLink,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";

import { useLogoutAction } from "../features/auth/useLogout";
import { HOME_PATH } from "../auth/redirectTarget";
import { useMe } from "../features/auth/useMe";
import { useEnabledModules } from "../features/modules/useEnabledModules";
import { StreamAlert } from "../features/notifications/StreamAlert";
import { buildCrumbs } from "./breadcrumbs";
import {
  BOTTOM_LEVEL,
  SETTINGS_ITEMS,
  TOP_LEVEL,
  isNavItemVisible,
} from "./navigation";
import type { NavItem } from "./navigation";

// Общий layout защищённых экранов.
//
// Mantine `AppShell`: header (лого + текущий user + «Выход») и navbar
// (постоянное меню по всем разделам). Контент роутов рендерится в
// `<Outlet/>` — layout-route оборачивает всю protected-ветку в
// `App.tsx`, поэтому шапка/меню не дублируются на каждой странице, а
// SSE-подписка нотификаций (в `ProtectedRoute`) монтируется один раз.
//
// Над `<Outlet/>` — хлебные крошки, построенные из текущего
// пути функцией `buildCrumbs`.
//
// Пункты меню фильтруются по составу включённых модулей
// (`useEnabledModules`, ADR-0083): пока состав грузится или упал, видны
// только разделы ядра. Таблица «раздел → модуль» — `navigation.ts`.

export function AppLayout(): ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  const me = useMe();
  const modules = useEnabledModules();
  // Burger управляет сворачиванием navbar на узких экранах; на
  // десктопе navbar всегда раскрыт (collapsed.desktop = false).
  const [opened, { toggle, close }] = useDisclosure();

  // Вынесено в `useLogoutAction` (`SelectOrgPage`, экран нулевого
  // состояния, тоже нуждается в выходе, а `AppLayout` там не
  // смонтирован) — поведение бит-в-бит прежнее.
  const handleLogout = useLogoutAction();

  // Активный пункт меню — по точному совпадению пути.
  const renderNavLink = (item: NavItem): ReactElement => (
    <NavLink
      key={item.to}
      component={Link}
      to={item.to}
      label={t(item.labelKey)}
      active={location.pathname === item.to}
      onClick={close}
    />
  );

  const isVisible = (item: NavItem): boolean =>
    isNavItemVisible(item, modules.isEnabled);

  const crumbs = buildCrumbs(location.pathname);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={t("nav.toggleMenu")}
            />
            <Anchor
              component={Link}
              to={HOME_PATH}
              underline="never"
              c="inherit"
            >
              <Title order={3}>{t("app.title")}</Title>
            </Anchor>
          </Group>

          <Group gap="md" wrap="nowrap">
            {me.data ? (
              <Text size="sm" c="dimmed" visibleFrom="xs">
                {me.data.user_email}
              </Text>
            ) : null}
            <Button
              color="red"
              variant="light"
              size="xs"
              onClick={handleLogout}
            >
              {t("nav.logout")}
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {TOP_LEVEL.filter(isVisible).map(renderNavLink)}

        <NavLink label={t("nav.settings")} defaultOpened childrenOffset={28}>
          {SETTINGS_ITEMS.filter(isVisible).map(renderNavLink)}
        </NavLink>

        {BOTTOM_LEVEL.filter(isVisible).map(renderNavLink)}
      </AppShell.Navbar>

      <AppShell.Main>
        <StreamAlert />
        {crumbs.length > 0 ? (
          <Breadcrumbs mb="md">
            {crumbs.map((crumb, index) => {
              const label = t(crumb.labelKey, crumb.params ?? {});
              const isLast = index === crumbs.length - 1;
              if (crumb.to !== undefined && !isLast) {
                return (
                  <Anchor key={crumb.labelKey} component={Link} to={crumb.to}>
                    {label}
                  </Anchor>
                );
              }
              // Последняя крошка — текущая страница (не ссылка, обычный
              // цвет); промежуточная без своего роута («Настройки») —
              // приглушённый текст.
              if (isLast) {
                return <Text key={crumb.labelKey}>{label}</Text>;
              }
              return (
                <Text key={crumb.labelKey} c="dimmed">
                  {label}
                </Text>
              );
            })}
          </Breadcrumbs>
        ) : null}

        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
